import useAuthStore from '@/src/hooks/auth';
import useConfigureRevenueCat from '@/src/modules/pricing/hooks/useConfigureRevenueCat';
import { useCurrentDeviceStore } from '@/src/modules/pricing/hooks/useCurrentDeviceStore';
import { useIsRevenueCatAvailable } from '@/src/modules/pricing/hooks/useIsRevenueCatAvailable';
import {
  PurchaseError,
  PurchaseErrorCode,
  type PurchaseType,
} from '@fabric-app/business/pricing/pricing.types';
import { pricingQueryKeys } from '@/src/modules/pricing/queries/pricingQueryKeys';
import type { SubscriptionTier } from '@fabric/woody-client';
import type {
  CustomerInfo,
  MakePurchaseResult,
  PurchasePackageOptions,
} from '@revenuecat/purchases-capacitor';
import { Purchases, PURCHASES_ERROR_CODE } from '@revenuecat/purchases-capacitor';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { mutate } from 'swr';
import { shallow } from 'zustand/shallow';
import { useOptimisticallyUpdateQueriesWithPredicate } from '@fabric-app/queries/utilities';
import { pick } from '@fabric-app/utils/object/manipulation';
import { ensureError, isError } from '@fabric-app/utils/guards';
import { toast } from '@fabric-app/business/store/alerts';
import { userQueryKeys } from '@fabric-app/business/user/queries/userQueryKeys';

type MutationVariables = {
  options: PurchasePackageOptions;
  type: PurchaseType;

  optimistic: {
    tier: SubscriptionTier;
    billingCycle: string;
  };
};

/**
 * Will purchase a package using RevenueCat (subscription) and update the user's subscription.
 * **DON'T USE THIS DIRECTLY**: @see useMutationPurchase instead which has extra conditions and checks.
 */
export const useMutationRevenueCatPurchase = () => {
  const configureRevenueCat = useConfigureRevenueCat();
  const optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  const isRevenueCatAvailable = useIsRevenueCatAvailable();
  const currentDeviceStore = useCurrentDeviceStore();
  const router = useRouter();
  const queryClient = useQueryClient();

  const { user, setUser, setForcedOptimisticSubscription } = useAuthStore(
    (s) => pick(s, ['user', 'setUser', 'setForcedOptimisticSubscription']),
    shallow,
  );

  const mutation = useMutation({
    mutationFn: async ({ options }: MutationVariables): Promise<MakePurchaseResult> => {
      if (!isRevenueCatAvailable) {
        throw new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore);
      }

      await configureRevenueCat();

      try {
        return await Purchases.purchasePackage(options);
      } catch (e) {
        if (isError(e) && 'code' in e && e.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
          throw new PurchaseError(PurchaseErrorCode.CANCELLED, currentDeviceStore);
        }

        throw ensureError(e);
      }
    },
    onSuccess: (result, { optimistic, type }) => {
      optimisticallyUpdateQueries<CustomerInfo>(
        (q) => q.queryKey[0] === pricingQueryKeys.revenueCatCustomerInfo()[0],
        () => result.customerInfo,
      );

      if (user) {
        setUser({
          ...user,
          subscription: {
            ...user.subscription,
            tier: optimistic.tier,
            billingCycle: optimistic.billingCycle,
            store: currentDeviceStore,
            storeId: result.productIdentifier,
          },
        });

        setForcedOptimisticSubscription({
          ...user.subscription,
          tier: optimistic.tier,
          billingCycle: optimistic.billingCycle,
          store: currentDeviceStore,
          storeId: result.productIdentifier,
        });
      }

      /**
       * unfortunately server returns old value, probably takes time to process (webhooks and what not)
       * so we need to wait a bit before revalidating the woody-user
       */
      setTimeout(() => {
        mutate('woody-user');
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.userInfo(),
        });
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.quota(),
        });
      }, 1000);

      if (type === 'first') {
        router.push('/thank-you');
      } else {
        toast({
          content: 'Your subscription has been updated.',
        });
      }
    },
  });

  return mutation;
};
